import React, { FC, useState, useEffect } from "react";
import { Card, Row, Col, Statistic, Spin, Alert, Typography, Table, Tag, Space } from "antd";
import { fetchCohortData } from "../actions/overviewActions";
import { CohortAnalysisData } from "../types/overview";
import { useFilters } from "../hooks/useFilters";
import AnalyticsFilters from "./AnalyticsFilters";

const { Title, Text } = Typography;

const CohortAnalysis: FC = () => {
  const [loading, setLoading] = useState(true);
  const [cohortData, setCohortData] = useState<CohortAnalysisData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { filters, filterOptions, handleDateRangeChange, handleFilterChange } = useFilters();

  useEffect(() => {
    loadCohortData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadCohortData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchCohortData(filters);
      setCohortData(data);
    } catch (err) {
      setError("Failed to load cohort data");
      console.error("Cohort data loading error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getRetentionColor = (rate: number): string => {
    if (rate >= 80) return "green";
    if (rate >= 60) return "blue";
    if (rate >= 40) return "orange";
    if (rate >= 20) return "volcano";
    return "red";
  };

  const columns = [
    {
      title: "Cohort Date",
      dataIndex: "cohort_date",
      key: "cohort_date",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Cohort Size",
      dataIndex: "cohort_size",
      key: "cohort_size",
      render: (size: number) => size.toLocaleString(),
    },
    {
      title: "Day 1 Retention",
      key: "day_1",
      render: (record: any) => (
        <Space direction="vertical" size="small">
          <Tag color={getRetentionColor(record.day_1.retention_rate)}>{record.day_1.retention_rate}%</Tag>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.day_1.retained_users}/{record.day_1.total_users}
          </Text>
        </Space>
      ),
    },
    {
      title: "Day 7 Retention",
      key: "day_7",
      render: (record: any) => (
        <Space direction="vertical" size="small">
          <Tag color={getRetentionColor(record.day_7.retention_rate)}>{record.day_7.retention_rate}%</Tag>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.day_7.retained_users}/{record.day_7.total_users}
          </Text>
        </Space>
      ),
    },
    {
      title: "Day 30 Retention",
      key: "day_30",
      render: (record: any) => (
        <Space direction="vertical" size="small">
          <Tag color={getRetentionColor(record.day_30.retention_rate)}>{record.day_30.retention_rate}%</Tag>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.day_30.retained_users}/{record.day_30.total_users}
          </Text>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
          <p>Loading cohort analysis...</p>
        </div>
      </Card>
    );
  }

  if (error || !cohortData) {
    return (
      <Card>
        <Alert message="Error" description={error || "No cohort data available"} type="error" showIcon />
      </Card>
    );
  }

  return (
    <div>
      <AnalyticsFilters
        filters={filters}
        filterOptions={filterOptions}
        onDateRangeChange={handleDateRangeChange}
        onFilterChange={handleFilterChange}
        loading={loading}
      />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Title level={3}>Cohort Retention Analysis</Title>
            <Text type="secondary">Track user retention rates across different time periods (Day 1, 7, and 30)</Text>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={8}>
                <Statistic
                  title="Total Cohorts"
                  value={cohortData.summary.total_cohorts}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Users Analyzed"
                  value={cohortData.summary.total_users_analyzed}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Average Day 1 Retention"
                  value={cohortData.summary.avg_day_1_retention}
                  suffix="%"
                  precision={2}
                  valueStyle={{ color: "#722ed1" }}
                />
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Statistic
                  title="Average Day 7 Retention"
                  value={cohortData.summary.avg_day_7_retention}
                  suffix="%"
                  precision={2}
                  valueStyle={{ color: "#fa8c16" }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Average Day 30 Retention"
                  value={cohortData.summary.avg_day_30_retention}
                  suffix="%"
                  precision={2}
                  valueStyle={{ color: "#eb2f96" }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="Cohort Retention Table">
            <Table
              columns={columns}
              dataSource={cohortData.cohorts}
              rowKey="cohort_date"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} cohorts`,
              }}
              scroll={{ x: true }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="Retention Rate Legend" size="small">
            <Space wrap>
              <Tag color="green">80%+ Excellent</Tag>
              <Tag color="blue">60-79% Good</Tag>
              <Tag color="orange">40-59% Average</Tag>
              <Tag color="volcano">20-39% Poor</Tag>
              <Tag color="red">&lt;20% Critical</Tag>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CohortAnalysis;
